/**
 * In-process closure-backed store for the WorkOS access + refresh
 * tokens. Replaces the previous `localStorage` keys (`auth_token`,
 * `refresh_token`, `workos_user`) which were visible to any code that
 * could open DevTools or read `localStorage` directly.
 *
 * Pairs with `authFetchShim.js`: the fetch shim captures token-bearing
 * responses from `/v1/auth/*` and writes them here; the rest of the
 * client (authBridge, restClient, logout) reads from here. Nothing
 * outside this module holds a long-lived reference to the tokens.
 *
 * Threat model this defends:
 *   - DevTools "look at localStorage" inspection — no longer surfaces
 *     the tokens.
 *   - Third-party scripts reading the well-known `auth_token` /
 *     `refresh_token` storage keys — the keys are no longer written.
 *
 * Threat model this does NOT defend:
 *   - In-context XSS payloads that can monkey-patch the fetch wrapper
 *     before it runs or run an attached debugger. Closures are
 *     hard-to-reach, not unreachable. The long-term answer is HttpOnly
 *     cookies / sender-constrained tokens; tracked separately.
 */

let _accessToken = null;
let _refreshToken = null;
let _userMetadata = null;
let _authContextEpoch = 0;
let _authIdentityEpoch = 0;
const _authContextSubscribers = new Set();
const _authIdentitySubscribers = new Set();

function notifyAuthContextChanged() {
    _authContextEpoch += 1;
    for (const subscriber of _authContextSubscribers) {
        try { subscriber(); }
        catch (error) { console.warn("[auth] context-change subscriber failed:", error); }
    }
}

function notifyAuthIdentityChanged() {
    _authIdentityEpoch += 1;
    for (const subscriber of _authIdentitySubscribers) {
        try { subscriber(); }
        catch (error) { console.warn("[auth] identity-change subscriber failed:", error); }
    }
}

export function effectiveTenantBinding(claims) {
    if (claims?.org_id != null) {
        if (typeof claims.org_id !== "string" || claims.org_id.length === 0) return null;
        return `workos:${claims.org_id}`;
    }
    if (!Array.isArray(claims?.groups)) return null;
    for (const group of claims.groups) {
        if (typeof group !== "string") return null;
        const normalized = group.replace(/^\/+/, "");
        const separator = normalized.indexOf(":");
        if (separator >= 0) return `keycloak:${normalized.slice(0, separator)}`;
    }
    return null;
}

function claimsFromJwt(token) {
    if (typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        const json = atob(padded);
        const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
        return null;
    }
}

export function sessionBindingFromJwt(token) {
    const claims = claimsFromJwt(token);
    if (
        typeof claims?.iss !== "string" || claims.iss.length === 0
        || typeof claims?.sub !== "string" || claims.sub.length === 0
        || typeof claims?.sid !== "string" || claims.sid.length === 0
    ) return null;
    const tenant = effectiveTenantBinding(claims);
    if (tenant == null) return null;
    return { issuer: claims.iss, subject: claims.sub, sessionId: claims.sid, tenant };
}

function canonicalClaims(value) {
    if (Array.isArray(value)) return value.map(canonicalClaims);
    if (value !== null && typeof value === "object") {
        return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonicalClaims(value[key])]));
    }
    return value;
}

function dataContextKey(token) {
    // Parsing here only controls cache reuse; the server validates credentials.
    // Unknown bindings invalidate conservatively. Preserve every claim except
    // token lifetime/identifier fields so authorization changes still invalidate.
    if (sessionBindingFromJwt(token) == null) return null;
    const { iat, exp, jti, ...claims } = claimsFromJwt(token);
    return JSON.stringify(canonicalClaims(claims));
}

function bindingKey(token) {
    const binding = sessionBindingFromJwt(token);
    return binding == null
        ? null
        : `${binding.issuer}\u0000${binding.subject}\u0000${binding.sessionId}\u0000${binding.tenant}`;
}

export function getAccessToken() {
    return _accessToken;
}

export function getRefreshToken() {
    return _refreshToken;
}

export function getUserMetadata() {
    return _userMetadata;
}

// Monotonic, non-secret discriminator for caches and mounted data regions.
// The access token itself must never become part of a cache key: keys are easy
// to inspect in a debugger and often turn up in diagnostics. A generation has
// the same isolation property without retaining another copy of the token.
export function getAuthContextEpoch() {
    return _authContextEpoch;
}

export function onAuthContextChange(subscriber) {
    _authContextSubscribers.add(subscriber);
    return () => _authContextSubscribers.delete(subscriber);
}

// Stable identity/tenant discriminator for remounting long-lived UI state.
// Authorization changes can invalidate data without remounting this identity.
export function getAuthIdentityEpoch() {
    return _authIdentityEpoch;
}

export function onAuthIdentityChange(subscriber) {
    _authIdentitySubscribers.add(subscriber);
    return () => _authIdentitySubscribers.delete(subscriber);
}

// Set tokens. Either field is optional — refresh updates may omit
// `refresh` (the same refresh token is reused), and the user field is
// only populated on the initial exchange / org-selection response.
// Passing an explicit `null` clears that slot.
export function setTokens({ access, refresh, user } = {}) {
    const oldAccess = _accessToken;
    const oldUser = _userMetadata;
    if (access !== undefined) _accessToken = access;
    if (refresh !== undefined) _refreshToken = refresh;
    if (user !== undefined) _userMetadata = user;
    const accessChanged = access !== undefined && access !== oldAccess;
    const userChanged = user !== undefined && user !== oldUser;
    if (accessChanged || userChanged) {
        const oldContext = dataContextKey(oldAccess);
        const newContext = dataContextKey(_accessToken);
        if (userChanged || oldContext == null || newContext == null || oldContext !== newContext) {
            notifyAuthContextChanged();
        }
    }
    if (accessChanged || userChanged) {
        const oldBinding = bindingKey(oldAccess);
        const newBinding = bindingKey(_accessToken);
        // Opaque/unbound tokens cannot prove a same-identity refresh, so fail
        // closed and remount when they change.
        if (userChanged || oldBinding == null || newBinding == null || oldBinding !== newBinding) {
            notifyAuthIdentityChanged();
        }
    }
}

export function clearTokens() {
    const changed = _accessToken !== null || _refreshToken !== null || _userMetadata !== null;
    _accessToken = null;
    _refreshToken = null;
    _userMetadata = null;
    if (changed) {
        notifyAuthContextChanged();
        notifyAuthIdentityChanged();
    }
}

// True when the store currently holds an access token. Used by
// bootstrap to decide whether the page can render.
export function hasAccessToken() {
    return typeof _accessToken === "string" && _accessToken.length > 0;
}
